import { observable, computed, action, runInAction } from 'mobx';
import { Duration } from 'luxon';
import { TrainigProjectSessionDto, TrainingSessionStatus, TrainingProjectSessionLogDto } from '../types';
import { TrainingProjectDashboardStore } from '../stores';
import { TrainingProjectsService } from '../services';

type Status = TrainingSessionStatus;

type LogsSource = 'stored' | 'stream';

const MAX_ERROR_LENGTH = 500;

export default class TrainingProjectSessionModel {
    @observable
    status: Status;

    @observable
    modelFilePath?: string;

    @observable
    parameters?: Record<string, unknown>;

    @observable
    metrics?: Record<string, unknown>;

    @observable
    error?: string;

    @observable
    updateDate?: Date;

    @observable
    startDate?: Date;

    @observable
    endDate?: Date;

    @observable
    logs: TrainingProjectSessionLogDto[] = [];

    @observable
    isLoadingLogs: boolean = false;

    @observable
    logsSource: LogsSource = 'stream';

    @observable
    elapsedTimeFormatted: string | null = null;

    @observable
    internalNote?: string;

    readonly id: string;
    readonly trainingProjectId: string;
    readonly createDate: Date;
    readonly createdBy: string;

    constructor(
        private readonly store: TrainingProjectDashboardStore,
        private readonly service: TrainingProjectsService,
        data: TrainigProjectSessionDto
    ) {
        this.id = data.id;
        this.trainingProjectId = data.trainingProjectId;
        this.status = data.status;
        this.modelFilePath = data.modelFilePath;
        this.parameters = data.parameters;
        this.metrics = data.metrics;
        this.error = data.error;
        this.createDate = new Date(data.createDate);
        this.createdBy = data.createdBy;
        this.updateDate = data.updateDate ? new Date(data.updateDate) : undefined;
        this.startDate = data.startDate ? new Date(data.startDate) : undefined;
        this.endDate = data.endDate ? new Date(data.endDate) : undefined;
        this.internalNote = data.internalNote;

        this.handleSessionMonitoring();
        this.updateElapsedTime();
    }

    @computed
    get trainingProject() {
        return this.store.trainingProject;
    }

    @computed
    get isCancelable() {
        return this.status === 'Running';
    }

    @computed
    get isCompleted() {
        return this.status === 'Succeeded' || this.status === 'Failed' || this.status === 'Canceled';
    }

    @computed
    get storedLogs(): string[] {
        return this.logs.map(log => log.message);
    }

    @computed
    get streamLogs(): string[] {
        return this.store.streamLogs.get(this.id) ?? [];
    }

    @computed
    get logsToDisplay(): string[] {
        const logsToDisplay = this.logsSource === 'stream' ? this.streamLogs : this.storedLogs;
        return this.error ? [...logsToDisplay, this.error] : logsToDisplay;
    }

    @action.bound
    setLogsSource(logsSource: LogsSource) {
        this.logsSource = logsSource;
    }

    @action.bound
    async loadData() {
        if (!this.trainingProject) {
            return;
        }

        const resp = await this.service.getTrainingSession(
            this.trainingProject.projectId,
            this.trainingProjectId,
            this.id
        );

        if (!resp.isOk()) {
            return;
        }

        resp.map(data => this.updateData(data));
    }

    @action.bound
    async cancel() {
        if (this.isCancelable) {
            await this.service.cancelTraining(this.id);
        }
    }

    @action.bound
    async downloadTrainedModel() {
        if (!this.trainingProject || !this.modelFilePath) {
            return;
        }

        await this.service.downloadTrainedModel(
            this.trainingProject.projectId,
            this.trainingProjectId,
            this.id,
            this.modelFilePath
        );
    }

    @action.bound
    async loadLogs() {
        try {
            if (!this.trainingProject) {
                return;
            }

            runInAction(() => {
                this.isLoadingLogs = true;
            });

            const resp = await this.service.getTrainingSessionLogs(
                this.trainingProject.projectId,
                this.trainingProjectId,
                this.id
            );

            if (!resp.isOk()) {
                return;
            }

            resp.map(logs =>
                runInAction(() => {
                    this.logs = logs;
                })
            );
        } finally {
            runInAction(() => {
                this.isLoadingLogs = false;
            });
        }
    }

    @action.bound
    updateElapsedTime(): void {
        if (!this.startDate) {
            return;
        }

        const milliseconds = Math.max(0, (this.endDate ?? new Date()).getTime() - this.startDate.getTime());
        const duration = Duration.fromMillis(milliseconds).shiftTo('hours', 'minutes', 'seconds');
        const hours = Math.floor(duration.hours);
        const minutes = Math.floor(duration.minutes);
        const seconds = Math.floor(duration.seconds);
        const formatTimeDigit = (n: number) => (n < 10 ? `0${n}` : `${n}`);

        this.elapsedTimeFormatted = `${formatTimeDigit(hours)}:${formatTimeDigit(minutes)}:${formatTimeDigit(seconds)}`;
    }

    @action.bound
    async updateNote(note: string | null) {
        if (!this.trainingProject) {
            return;
        }

        const resp = await this.service.updateSessionNote(
            this.trainingProject.projectId,
            this.trainingProjectId,
            this.id,
            note
        );

        if (resp.isOk()) {
            runInAction(() => {
                this.internalNote = note || undefined;
            });
        }

        return resp;
    }

    handleSessionMonitoring() {
        if (this.isCompleted) {
            return this.clearIntervals();
        }

        this.handleSessionPolling();
        this.handleSessionStreamingLogs();
        this.handleSessionElapsedTime();
    }

    handleSessionPolling() {
        if (!this.store.sessionPollingIntervals.has(this.id)) {
            this.store.addSessionPollingInterval(
                this.id,
                setInterval(() => this.loadData(), 3000)
            );
        }
    }

    handleSessionStreamingLogs() {
        if (!this.store.streamLogs.has(this.id)) {
            this.store.startStreamingLogs(this.id);
        }
    }

    handleSessionElapsedTime() {
        if (this.startDate && !this.endDate && !this.store.sessionElapsedTimeIntervals.has(this.id)) {
            this.updateElapsedTime();
            this.store.addSessionElapsedTimeInterval(
                this.id,
                setInterval(() => this.updateElapsedTime(), 1000)
            );
        }

        if (this.endDate && this.store.sessionElapsedTimeIntervals.has(this.id)) {
            this.updateElapsedTime();
            this.store.removeSessionElapsedTimeInterval(this.id);
        }
    }

    clearIntervals() {
        this.store.removeSessionPollingInterval(this.id);
        this.store.removeSessionElapsedTimeInterval(this.id);
    }

    updateData(data: TrainigProjectSessionDto) {
        runInAction(() => {
            this.status = data.status;
            this.modelFilePath = data.modelFilePath;
            this.metrics = data.metrics;
            this.error = data.error;
            this.updateDate = data.updateDate ? new Date(data.updateDate) : undefined;
            this.startDate = data.startDate ? new Date(data.startDate) : undefined;
            this.endDate = data.endDate ? new Date(data.endDate) : undefined;
            this.internalNote = data.internalNote;
        });

        this.handleSessionMonitoring();
    }

    @computed
    get serializedMetrics() {
        return this.metrics ? JSON.stringify(this.metrics) : null;
    }

    @computed
    get sanitizedNotePreview() {
        if (!this.internalNote) {
            return null;
        }

        const sanitized = this.internalNote.replace(/\s+/g, ' ').trim();

        return sanitized.length > 200 ? sanitized.substring(0, 200) + '…' : sanitized;
    }

    @computed
    get formattedError() {
        if (!this.error) {
            return null;
        }

        return this.error.length > MAX_ERROR_LENGTH ? `...${this.error.slice(-MAX_ERROR_LENGTH)}` : this.error;
    }
}
